import React from 'react';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classnames from 'classnames';
import { OERadioOptions } from '../../../../../../../global/oe-radio-options/oe-radio-options';
import { ICustomFieldsDTO } from '../../../../../../../../api/custom-fields.dao';
import { OEToggle } from '../../../../../../../global/oe-toggle/oe-toggle';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import Text from '../../../../../../../global/text/text';
import { CustomRadioValues } from '../custom-field.presentation';
import { ChevronUpIcon, LockIcon } from '../../../../../../../global/icons/icons';
import CustomFieldVM, { CustomFieldType } from '../custom-field.viewmodel';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import { Space } from '../../../../../../../../constants/html-character-entities';
import SelectTextList from '../../../../../../../global/select-text-list/select-text-list';
import OECheckbox from '../../../../../../../global/oe-checkbox/oe-checkbox';

interface IProps {
    field: ICustomFieldsDTO;
    intl: IntlShape;
    updateField: (id: number, field: ICustomFieldsDTO) => void;
    getFieldErrorMessage: (text: string, isMandatory?: boolean, isAlphaNumeric?: boolean) => string;
    toggleVisibility: (id: number, field: ICustomFieldsDTO) => string | null;
    combineOptionsArrayIntoString: (options: string[]) => string;
    getOptionsFieldErrorMessage: (field: ICustomFieldsDTO) => string | null;
    getOptionsArray: (text: string) => string[];
    getShortRichTextValidationError: (text: string) => string | null;
    getShortPlainValidationError?: (text: string) => string | null;
    vm: CustomFieldVM;
}

export const FieldToggle = {
    displayed: 'Field is displayed',
    hidden: ' is hidden'
};

export const CustomFieldDefault = injectIntl(mobxReact.observer((props: IProps): JSX.Element => {
    const {
        field,
        intl,
        toggleVisibility,
        getFieldErrorMessage,
        updateField,
        combineOptionsArrayIntoString,
        getOptionsFieldErrorMessage,
        getOptionsArray,
        getShortRichTextValidationError,
        vm
    } = props;

    const {
        label,
        description,
        canBeOptional,
        defaultFieldId,
        externalFieldId,
        defaultFieldTitle,
        customFieldId,
        default: isDefault,
        isCollapsed,
        type,
        optionsList,
        triaged
     } = field;

    const visible = vm.isFieldVisible(field);

    const id = defaultFieldId ?? customFieldId;
    const title = externalFieldId ?? defaultFieldTitle;

    const updateDefaultField = mobx.action((property, value) => {
        updateField(id, { ...field, [property]: value});
    });

    const collapseChevronClasses = classnames({
        'collapsed': isCollapsed,
        'centered': !visible
    });

    const addAnswer = mobx.action((value: string) => {
         const answers = [...optionsList];
         answers.push(value.trim());
         updateDefaultField('optionsList', answers);
         if (triaged) vm.onAnswerOptionAdded(value);
    });

    const removeAnswer = mobx.action((value: string) => {
        const answers = [...optionsList];
        const indexOption = answers.indexOf(value);
        answers.splice(indexOption, 1);
        updateDefaultField('optionsList', answers);
        if (triaged) vm.onAnswerOptionRemoved(value);
    });

    const isOptionChecked = (key: string): boolean => field.optionsList.includes(key);

    return (
        <div className={collapseChevronClasses}>
            <div className='custom-field__content'>
                <label onClick={() => updateDefaultField('isCollapsed', !isCollapsed)} className='custom-field__title'>
                    <span>
                        {title}
                        {triaged &&
                            <>
                                <Space />
                                <span>
                                    ({intl.formatMessage({
                                        id:'oe.triageQuestion.title',
                                        defaultMessage:'Triage Question',
                                        description:'Button text'
                                    })})
                                </span>
                            </>
                        }
                    </span>
                    <ChevronUpIcon/>
                </label>
                {!isCollapsed &&
                    <div className='custom-field__list'>
                        { type === CustomFieldType.dropdown && !triaged && <FormField
                            className='m-bottom-20px'
                            label={<FormattedMessage
                                id='oe.customField.optionsList'
                                defaultMessage='List Options'
                                description='Label text'
                            />}
                        >
                            <textarea
                                className='oe-form-field__control'
                                value={combineOptionsArrayIntoString(optionsList)}
                                onChange={mobx.action(({ target: { value } }) => updateDefaultField('optionsList', getOptionsArray(value)))}
                            />
                            { getOptionsFieldErrorMessage(field) &&
                                <span className='oe-form-field__error'>
                                    {getOptionsFieldErrorMessage(field)}
                                </span>
                            }
                            <p className='oe-form-field__info'>
                                <FormattedMessage
                                    id='oe.customField.enterEachListOptionOnANewLineWithoutEmpty'
                                    description='Input info text'
                                    defaultMessage='Enter each list option on a new line, at least one option should be added'
                                />
                            </p>
                        </FormField>}
                        <FormField
                            labelFor={`label-${id}`}
                            className='m-bottom-20px'
                            label={<FormattedMessage
                                id='oe.label.fieldLabel'
                                description='Label text'
                                defaultMessage='Field Label'
                            />}
                        >
                            { type === CustomFieldType.checkbox ? (
                                <RichTextEditor
                                    value={label}
                                    onChange={(_, editor) => updateDefaultField('label', editor.getData())}
                                    className='custom-field-checkbox-label'
                                    validationError={getShortRichTextValidationError(label)}
                                />
                            ) : (
                                <>
                                <input
                                    id={`label-${id}`}
                                    className='oe-form-field__control'
                                    value={label}
                                    type='text'
                                    onChange={({ target: { value } }) => updateDefaultField('label', value)}
                                />
                                    {getFieldErrorMessage(label, true) &&
                                    <span className='oe-form-field__error'>
                                        <FormattedMessage
                                            id='oe.label.fieldLabelError'
                                            description='Label text'
                                            defaultMessage='Field Label {suffix}'
                                            values={{
                                                suffix:  getFieldErrorMessage(label, true)
                                            }}
                                        />
                                    </span>
                                    }
                                </>
                            )}
                        </FormField>
                        {!isDefault &&
                            <FormField
                                labelFor={`description-${id}`}
                                className='m-bottom-20px'
                                label={<FormattedMessage
                                    id='oe.label.fieldDescription'
                                    description='Field label text'
                                    defaultMessage='Description'
                                />}>
                                <textarea
                                    id={`description-${id}`}
                                    className='oe-form-field__control'
                                    value={description}
                                    onChange={({ target: { value } }) => updateDefaultField('description', value)}
                                />
                                {getFieldErrorMessage(description) &&
                                <span className='oe-form-field__error'>
                                    <FormattedMessage
                                        id='oe.label.fieldDescriptionError'
                                        description='Label text'
                                        defaultMessage='Description {suffix}'
                                        values={{
                                            suffix:  getFieldErrorMessage(description)
                                        }}
                                    />
                                </span>
                                }
                            </FormField>
                        }

                        { type === CustomFieldType.dropdown && triaged &&
                        <>
                            <SelectTextList
                                label={
                                    <FormattedMessage
                                        description='Label text'
                                        defaultMessage='Other Answer Options'
                                        id='oe.triageQuestion.otherAnswerOptions'
                                    />
                                }
                                optionsList={field.optionsList}
                                addOption={addAnswer}
                                removeOption={removeAnswer}
                                getFieldErrorMessage={getFieldErrorMessage}
                                optionsInvalidErrorMessage={vm.getTriageOptionsErrorMessage(field.optionsList)}
                                required={true}
                            />
                        </>
                        }
                        { type === CustomFieldType.attendance_method &&
                            <>
                                <FormField
                                    className='custom-field__attendance-method-options m-bottom-20px'
                                    label={<FormattedMessage
                                        id='oe.triageQuestion.answerOptions'
                                        description='Label text'
                                        defaultMessage='Answer Options' />
                                    }
                                >{Object.entries(vm.attendanceMethodOptions)
                                    .map(([key, value]) => {
                                        return (
                                            <OECheckbox
                                                key={key}
                                                label={<FormattedMessage
                                                    id={`oe.attendanceMethodOption.${key}`}
                                                    defaultMessage={value}
                                                    description='Label text' />
                                                }
                                                checked={isOptionChecked(key)}
                                                onChange={() => isOptionChecked(key) ? removeAnswer(key) : addAnswer(key)}
                                                classNames='oe-form-field__checkbox attendee-form__checkbox'
                                            />
                                        );
                                    })}
                                    {!vm.isAttendanceMethodOptionsValid(field) &&
                                        <span className='oe-form-field__error'>
                                            <FormattedMessage
                                                id='oe.validation.error.attendanceMethodOptions'
                                                description='Validation message'
                                                defaultMessage='At least one Answer Option must be enabled.'
                                            />
                                        </span>
                                    }
                                </FormField>
                            </>
                        }
                        {!vm.isTriagedNoSelectionForm && !triaged && (!isDefault || canBeOptional) && <FormField className='m-bottom-20px'>
                            <OERadioOptions
                                options={[
                                    {
                                        value: CustomRadioValues.optional,
                                        label: CustomRadioValues.optional
                                    },
                                    {
                                        value: CustomRadioValues.required,
                                        label: CustomRadioValues.required
                                    }
                                ]}
                                value={vm.isFieldMandatory(field) ? CustomRadioValues.required : CustomRadioValues.optional}
                                onOptionToggle={value => {
                                    vm.setIsFieldMandatory(field, CustomRadioValues.required === value);
                                }}
                                styles='website-custom__radio'
                                optionsId={`${id}-field`}
                            />
                        </FormField>}
                        {vm.canFieldBeHidden(field) && <FormField className='m-bottom-20px'>
                            <div className='website-custom__toggle'>
                                <OEToggle
                                    isSelected={visible}
                                    onClick={() => toggleVisibility(id, field)}
                                />
                                <Text message={intl.formatMessage({
                                    id: 'oe.customFields.fieldIsdisplayed',
                                    defaultMessage: FieldToggle.displayed,
                                    description: 'Field visibility label'
                                })}/>
                            </div>
                        </FormField>}
                    </div>
                }
                {!vm.canFieldBeHidden(field) &&
                    <div className='custom-field__lock-icon'>
                        <LockIcon/>
                    </div>
                }
            </div>
        </div>
    );
}));
